import { ColumnDef } from "@tanstack/react-table";

import { FC, MouseEventHandler, useEffect, useState } from "react";
import Table from "../../../../assets/components/Table/Table"
import TableHeader from "../../../../assets/components/Table/TableHeader"
import { Feature, FeatureList, Permissions } from "../../../../models/models";
import EditorFeatureProject from "../ActivationFeatureName/ProjectSubRowFeature";
import RemoveButton from "../../../../assets/components/RemoveButton/RemoveButton";
import { useAppSelector } from "../../../../assets/hooks/hooks";
import { hasPermission } from "../../../../assets/helpers/helpers";
import { useNotification } from "../../../../assets/hooks/useNotification";
import { actionText } from "../../../../text";

interface Props {
    projectFeatures: FeatureList;
    getFeaturesList: (features: FeatureList) => void;
}

export type StateRow = "default" | "create" | "update";

export const ProjectSubRowFeatures: FC<Props> = ({ projectFeatures, getFeaturesList }) => {
    const [features, setFeatures] = useState<FeatureList>(projectFeatures);
    const [currentFeature, setCurrentFeature] = useState<Feature | undefined>();
    const [counterNewFeature, setCounterNewFeature] = useState<number>(0);
    const { AttentionMessageBox } = useNotification()

    const user = useAppSelector((state) => state.initializeApp);

    const areAccessPermission = hasPermission(
        user.permissions,
        Permissions.PROJECT_EDIT
    )

    useEffect(() => {
        getFeaturesList(features);
    }, [features, getFeaturesList])

    const [stateRowFeatureTable, setStateRowFeatureTable] = useState<StateRow>("default");

    const editFeatureHandler = ({ id, name }: Feature) => {
        if (stateRowFeatureTable === "default") {
            const currentFeature = features.find(feature => feature.id === id) as Feature;
            setCurrentFeature(currentFeature);
            setStateRowFeatureTable("update");
        }

        if (stateRowFeatureTable === "update") {
            setStateRowFeatureTable("default");
        }

        if (stateRowFeatureTable === "create") {
            const currentFeature = features.filter(feature => feature.id !== id) as FeatureList;
            setFeatures(currentFeature);
            setStateRowFeatureTable("default");
        }
    };

    const updateFeatureListHandler = ({ id, name }: Feature) => {
        if (stateRowFeatureTable === "update" || stateRowFeatureTable === "create") {
            const updateFeatures = features.map((feature) => {
                if (feature.id === id) {
                    return { name, id: feature.id };
                }

                return feature
            }) as FeatureList;

            setFeatures(updateFeatures)
            setStateRowFeatureTable("default");
        }
    };

    const createNewFeatureHandler: MouseEventHandler<HTMLButtonElement> = () => {
        const newFeature: Feature = {
            id: counterNewFeature,
            name: "FeatureName",
        };

        if (stateRowFeatureTable !== "create") {
            setCounterNewFeature(counterNewFeature - 1)
            setCurrentFeature(newFeature);
            setFeatures((features) => [newFeature, ...features]);
            setStateRowFeatureTable("create");
        }
    };

    const removeFeatureHandler = (row: any) => (
        AttentionMessageBox({ textContent: actionText("delete", "feature") })
            .then((value) => {
                if (value === "yes") {
                    setFeatures((prevData) => [...prevData.filter((f) => f.id !== row.original.id)])
                }
            })
    )

    const columns: ColumnDef<Feature>[] = [
        {
            header: "Activation Feature Name",
            accessorKey: "name",
            cell: ({ row, getValue }) =>
                <EditorFeatureProject
                    id={row.original.id}
                    name={getValue<string>()}
                    state={row.original.id !== currentFeature?.id ? "default" : stateRowFeatureTable}
                    updateFeatureName={updateFeatureListHandler}
                    closeUpdateFeature={() => editFeatureHandler({ id: row.original.id, name: row.original.name })}
                />
        },
        {
            id: "expander",
            header: () => null,
            cell: ({ row }) => {
                if (!areAccessPermission) {
                    return
                }
                if (row.original.id === currentFeature?.id && stateRowFeatureTable === "create") {
                    return
                }
                return <button
                    type="button"
                    className="edit_button"
                    {...{
                        onClick: () => editFeatureHandler({ id: row.original.id, name: row.original.name }),
                        style: { cursor: "pointer" },
                    }}
                >
                    <span>Edit</span>
                </button>
            },
        },
        {
            id: "removed",
            header: () => null,
            cell: ({ row }) => {
                if (!areAccessPermission) {
                    return
                }

                if (row.original.id === currentFeature?.id && stateRowFeatureTable === "create") {
                    return
                }

                return <RemoveButton position="top_center" img="top" onClick={() => removeFeatureHandler(row)} />
            }
        },
    ];

    return <>
        <TableHeader
            title="List of features"
            onAddRowHandler={createNewFeatureHandler}
            addPermission={Permissions.PROJECT_EDIT}
            borderRadius="small"
        />

        <Table<Feature>
            borderRadius="small"
            className="project_features"
            data={features}
            columns={columns}
            getRowCanExpand={() => false}
        />
    </>
}
